import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

const CANDIDATES = 'a[href], button, input, select, textarea, [tabindex]';
const CONTROL = 'a[href], button, input, select, textarea';

function isTabbable(el: HTMLElement): boolean {
  if (el.tabIndex < 0 || el.matches(':disabled')) return false;
  if (el instanceof HTMLInputElement && el.type === 'hidden') return false;
  return el.closest('[hidden], [inert], [aria-hidden="true"]') === null;
}

function firstTabbable(container: HTMLElement): HTMLElement | null {
  for (const el of container.querySelectorAll<HTMLElement>(CANDIDATES)) {
    if (isTabbable(el)) return el;
  }
  return null;
}

/** Фокус «получилось», только если элемент действительно стал активным — скрытый
 *  (`visibility: hidden`) или отключённый элемент `focus()` молча игнорирует. */
function tryFocus(el: HTMLElement, preventScroll = true): boolean {
  el.focus({ preventScroll });
  return document.activeElement === el;
}

function activeElementOrNull(): HTMLElement | null {
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body ? active : null;
}

/**
 * Куда уходит клавиатурный фокус окна:
 *  - при открытии (и при разворачивании из свёрнутого) — на элемент с `data-autofocus`, иначе на
 *    первый доступный для Tab элемент содержимого (кнопки заголовка не считаются), иначе на само
 *    окно (`initialFocus="window"` — сразу на окно); если содержимое уже взяло фокус само
 *    (`autoFocus`) — не трогаем;
 *  - при переключении на окно с клавиатуры — туда, где пользователь был в нём в последний раз;
 *  - при сворачивании и закрытии — обратно на элемент, с которого окно открыли (строка списка,
 *    кнопка «Создать», поле другого окна), если он ещё на месте, а фокус никто не перехватил.
 *
 * Без этого окно открывается «в никуда» (фокус остаётся на странице под ним, и клавиши идут в
 * неё), а после закрытия фокус теряется на `body` и работать с клавиатуры в списке нельзя.
 */
export function useWindowFocus({
  rootRef,
  contentRef,
  footerRef,
  minimized,
  initialFocus = 'auto',
}: {
  rootRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  footerRef: RefObject<HTMLElement | null>;
  minimized: boolean;
  /** `'window'` — при открытии фокус на самом окне, а не на первом поле (например, документ
   *  открыт для просмотра: первое поле — календарь, который раскрылся бы сам). */
  initialFocus?: 'auto' | 'window';
}) {
  // Запоминаем при первом рендере: к эффектам монтирования `autoFocus` потомков уже перехватил бы фокус.
  const [opener] = useState(activeElementOrNull);
  const lastFocused = useRef<HTMLElement | null>(null);

  const focusInitial = useCallback((): boolean => {
    if (initialFocus === 'window') return rootRef.current ? tryFocus(rootRef.current) : false;
    const containers = [contentRef.current, footerRef.current].filter((c): c is HTMLElement => c !== null);
    for (const container of containers) {
      const marked = container.querySelector<HTMLElement>('[data-autofocus]');
      if (!marked) continue;
      const target = marked.matches(CONTROL) ? marked : firstTabbable(marked);
      if (target && tryFocus(target)) return true;
    }
    for (const container of containers) {
      const target = firstTabbable(container);
      if (target && tryFocus(target)) return true;
    }
    const root = rootRef.current;
    return root ? tryFocus(root) : false;
  }, [rootRef, contentRef, footerRef, initialFocus]);

  const restore = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const active = document.activeElement;
    if (active && active !== root && root.contains(active)) return;
    const last = lastFocused.current;
    if (last && last.isConnected && root.contains(last) && tryFocus(last)) return;
    focusInitial();
  }, [rootRef, focusInitial]);

  /** Сразу, а если не вышло — ещё раз в следующем кадре: только что развёрнутое окно ещё
   *  `visibility: hidden` (переход 120 мс), и `focus()` на него пока не действует. */
  const restoreSoon = useCallback(() => {
    restore();
    const root = rootRef.current;
    if (root && !root.contains(document.activeElement)) requestAnimationFrame(restore);
  }, [restore, rootRef]);

  const releaseToOpener = useCallback(() => {
    const root = rootRef.current;
    if (root && root.contains(document.activeElement) && opener?.isConnected) tryFocus(opener, false);
  }, [rootRef, opener]);

  useEffect(() => {
    if (minimized) releaseToOpener();
    else restoreSoon();
  }, [minimized, releaseToOpener, restoreSoon]);

  useEffect(() => {
    const root = rootRef.current;
    return () => {
      const active = document.activeElement;
      const lost = !active || active === document.body || (root?.contains(active) ?? false);
      if (lost && opener?.isConnected) tryFocus(opener, false);
    };
  }, [rootRef, opener]);

  /** Повесить на `onFocusCapture` окна: запоминает последний элемент содержимого с фокусом. */
  const rememberFocus = useCallback(
    (e: { target: EventTarget }) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (contentRef.current?.contains(target) || footerRef.current?.contains(target)) lastFocused.current = target;
    },
    [contentRef, footerRef],
  );

  return { restoreFocus: restoreSoon, rememberFocus };
}
